"use client";

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useRouter } from "next/navigation";

type AccessLevel = "none" | "view" | "manage" | "full";

const LEVEL_RANK: Record<AccessLevel, number> = { none: 0, view: 1, manage: 2, full: 3 };

function canDo(level: AccessLevel | undefined, action: string): boolean {
  const rank = LEVEL_RANK[level ?? "none"];
  switch (action) {
    case "view": return rank >= 1;
    case "create":
    case "update": return rank >= 2;
    case "delete": return rank >= 3;
    default: return false;
  }
}

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  roleName?: string;
  permissions?: Record<string, AccessLevel> | null;
}

interface AuthCtx {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasAccess: (feature: string, action?: string) => boolean;
}

const AuthContext = createContext<AuthCtx>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  hasAccess: () => true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const fetchMe = useCallback(async () => {
    try {
      let res = await fetch("/api/auth/me");
      if (!res.ok) {
        const refreshRes = await fetch("/api/auth/refresh", { method: "POST" });
        if (refreshRes.ok) {
          res = await fetch("/api/auth/me");
        }
      }
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const hasAccess = useCallback((feature: string, action: string = "view") => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (!user.permissions) return false;
    return canDo(user.permissions[feature], action);
  }, [user]);

  const login = async (email: string, password: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || "Login failed");
    }
    await fetchMe();
    router.push("/");
  };

  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    router.push("/login");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
